import MaxIntensityMarkerOverlay from './MaxIntensityMarkerOverlay';
import type { Environment } from '~/engine/data/Environment';
import { paneImageOf } from '~/engine/view/paneImage';
import { useFusedImageDisplayVersion } from '~/hooks/environment/useEnvironmentViews';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import { usePlaneViewHostedBy } from '../usePlaneViewHostedBy';
import { useCanvasFrameGeometryOf } from '../usePlaneViewOf';

interface TilePetMaxMarkerMountProps {
    renderer: RenderAdapter;
    /** The Environment whose tiles draw the marker; undefined hides it. */
    markerHost: Environment | undefined;
}

/**
 * Mounts the SUV threshold panel's maximum marker over one grid tile, and
 * only on a tile of the Environment that hosts it that shows the measured
 * image, the host's primary image, alone or blended. So the marker never
 * lands on another row, nor on a tile that draws the fused secondary image
 * alone, where it would mark the uptake on another series. A tile that
 * changes which image of the pair it draws is gated again at once. The
 * overlay owns projection and the on-plane treatment; this mount owns the
 * gate and passes the marker the frame its tile's canvas shows. Renders
 * nothing for non-engine renderers (the mock adapters).
 */
export default function TilePetMaxMarkerMount({
    renderer,
    markerHost,
}: TilePetMaxMarkerMountProps) {
    const view = usePlaneViewHostedBy(renderer, markerHost);
    useFusedImageDisplayVersion(markerHost);
    const frame = useCanvasFrameGeometryOf(renderer);
    if (!view || paneImageOf(view).kind !== 'primary') {
        return null;
    }
    return <MaxIntensityMarkerOverlay view={view} frame={frame} />;
}
